import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const faqs = [
  {
    q: 'Is the consultation free?',
    a: 'Yes. Both the consultation and custom demo are completely free.',
  },
  {
    q: 'Is the demo customized?',
    a: 'Yes. Every demo is built specifically for your business.',
  },
  {
    q: 'Is the ₹1,00,000 Platform Fee a setup charge?',
    a: 'No. It acts as prepaid credit and is adjusted against your future monthly invoices.',
  },
  {
    q: 'How are monthly charges calculated?',
    a: "They're based on your estimated and actual AI usage, such as voice calls, chat conversations, and workflow automations.",
  },
];

const steps = [
  {
    step: '01',
    title: 'Free Consultation',
    desc: 'Understand your business, goals, and automation requirements.',
    badge: 'Free',
    icon: 'ri-chat-3-line',
    accent: 'emerald',
  },
  {
    step: '02',
    title: 'Custom AI Demo',
    desc: 'We build a personalized demo tailored specifically to your business.',
    badge: 'Free',
    icon: 'ri-play-circle-line',
    accent: 'teal',
  },
  {
    step: '03',
    title: 'Platform Onboarding',
    desc: 'If you love the demo, we deploy your solution and fully configure everything for your business. Before deployment, we provide a projected monthly usage estimate based on your expected AI call and automation volumes.',
    badge: '₹1,00,000 Platform Fee',
    icon: 'ri-rocket-2-line',
    accent: 'cyan',
  },
  {
    step: '04',
    title: 'Monthly Billing',
    desc: 'Your Platform Fee acts as prepaid credit and is automatically adjusted against your monthly invoices. After the credit is utilized, billing continues based on your actual platform usage.',
    badge: 'Usage-Based',
    icon: 'ri-bill-line',
    accent: 'amber',
  },
];

const highlights = [
  'Free consultation',
  'Free custom demo',
  'Personalized usage estimate',
  'No hidden charges',
  'Transparent monthly billing',
];

export default function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const goToContact = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate('/#re-contact');
    const tryScroll = (attempts: number) => {
      const el = document.getElementById('re-contact');
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      if (attempts > 0) {
        setTimeout(() => tryScroll(attempts - 1), 150);
      }
    };
    setTimeout(() => tryScroll(5), 200);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* ── SECTION 1: HERO ── */}
      <section className="relative overflow-hidden bg-[#0a0a0a]">
        {/* Subtle dot-grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #4ade80 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        {/* Radial glow top-center */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(52,211,153,0.18) 0%, transparent 70%)',
          }}
        />
        {/* Bottom fade to white */}
        <div
          className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, #0a0a0a)' }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-36 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.1] mb-6">
            One Engine.{' '}
            <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Ultimate Power.
            </span>
            <br />
            Pay for what you use.
          </h1>

          <p className="text-lg md:text-xl text-white/65 max-w-2xl mx-auto mb-12 leading-relaxed font-normal">
            Get every feature from day one. High-performance AI Voice and Automation built for businesses that value their leads.
          </p>

          {/* Two feature callouts */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12">
            <div className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-6 text-left backdrop-blur-sm overflow-hidden group hover:border-emerald-500/30 transition-colors duration-300">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative z-10">
                <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/15 rounded-lg mb-4 border border-emerald-500/20">
                  <i className="ri-star-line text-emerald-400 text-sm"></i>
                </div>
                <p className="text-white font-semibold text-sm mb-2 tracking-tight">All Features Included</p>
                <p className="text-white/45 text-[13px] leading-relaxed font-normal">
                  No gated tiers. Every client gets our full stack — conversational AI voices, instant CRM sync, and multi-channel automation from day one.
                </p>
              </div>
            </div>
            <div className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-6 text-left backdrop-blur-sm overflow-hidden group hover:border-teal-500/30 transition-colors duration-300">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-teal-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative z-10">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-500/15 rounded-lg mb-4 border border-teal-500/20">
                  <i className="ri-shield-flash-line text-teal-400 text-sm"></i>
                </div>
                <p className="text-white font-semibold text-sm mb-2 tracking-tight">Built for Reliability</p>
                <p className="text-white/45 text-[13px] leading-relaxed font-normal">
                  Dedicated high-speed infrastructure, per project. Your AI agent is always on — zero lag, zero downtime, zero excuses.
                </p>
              </div>
            </div>
          </div>

          <a
            href="/#re-contact"
            onClick={goToContact}
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            Get a Custom Quote
          </a>
        </div>
      </section>

      {/* ── SECTION 2: HOW IT WORKS + FAQ ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <p className="text-emerald-600 font-medium text-sm uppercase tracking-widest mb-3">How It Works</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight mb-4">
              Simple, Performance-Based Billing
            </h2>
            <p className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Transparent pricing with no hidden charges. We build first, estimate your usage, and only then begin billing.
            </p>
          </div>

          {/* Two-column: Timeline + Highlight Panel */}
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-16 mb-16 md:mb-20">
            {/* Timeline */}
            <div className="flex-1 min-w-0">
              <div className="relative pl-10 sm:pl-12 md:pl-14">
                {/* Vertical line */}
                <div className="absolute left-[15px] sm:left-[18px] md:left-[22px] top-3 bottom-3 w-px bg-gray-200" />

                <div className="space-y-8 md:space-y-10">
                  {steps.map((s) => {
                    const accentColors: Record<string, { dot: string; badge: string; badgeText: string }> = {
                      emerald: { dot: 'bg-emerald-100 border-emerald-300 text-emerald-700', badge: 'bg-emerald-600 text-white border-emerald-600', badgeText: 'text-emerald-700' },
                      teal: { dot: 'bg-teal-100 border-teal-300 text-teal-700', badge: 'bg-teal-600 text-white border-teal-600', badgeText: 'text-teal-700' },
                      cyan: { dot: 'bg-cyan-100 border-cyan-300 text-cyan-700', badge: 'bg-gray-950 text-white border-gray-950', badgeText: 'text-cyan-700' },
                      amber: { dot: 'bg-amber-100 border-amber-300 text-amber-700', badge: 'bg-amber-500 text-white border-amber-500', badgeText: 'text-amber-700' },
                    };
                    const colors = accentColors[s.accent] || accentColors.emerald;

                    return (
                      <div key={s.step} className="relative group">
                        {/* Dot on timeline */}
                        <div className={`absolute -left-[34px] sm:-left-[40px] md:-left-[46px] top-1 w-9 h-9 md:w-11 md:h-11 rounded-full border-2 flex items-center justify-center text-[11px] md:text-xs font-bold ${colors.dot} transition-transform duration-300 group-hover:scale-110 z-10`}>
                          {s.step}
                        </div>

                        {/* Card */}
                        <div className="bg-white border border-gray-200 rounded-xl md:rounded-2xl p-4 sm:p-5 md:p-6 hover:border-gray-300 transition-all duration-300 group-hover:shadow-sm">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                                <i className={`${s.icon} text-gray-600 text-sm md:text-base`}></i>
                              </div>
                              <h3 className="text-base md:text-lg font-semibold text-gray-900">{s.title}</h3>
                            </div>
                            <span className={`inline-flex items-center rounded-full font-bold border whitespace-nowrap shadow-sm w-fit flex-shrink-0 ${s.step === '03' ? 'px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm' : 'px-3 sm:px-4 py-1 sm:py-1.5 text-[11px] sm:text-xs'} ${colors.badge}`}>
                              {s.badge}
                            </span>
                          </div>
                          <p className="text-gray-500 text-[13px] md:text-sm leading-relaxed sm:ml-12">{s.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Highlight Panel */}
            <div className="lg:w-[340px] flex-shrink-0">
              <div className="bg-gray-950 rounded-2xl p-6 md:p-8 lg:sticky lg:top-24">
                <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/8 rounded-full blur-3xl pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2.5 mb-5 md:mb-6">
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/20 rounded-lg">
                      <i className="ri-shield-check-line text-emerald-400 text-sm"></i>
                    </div>
                    <h3 className="text-white font-semibold text-base">What You Get</h3>
                  </div>
                  <ul className="space-y-3 md:space-y-4">
                    {highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-emerald-400 text-sm"></i>
                        </div>
                        <span className="text-gray-300 text-[13px] md:text-sm leading-relaxed">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto border-t border-gray-100 pt-12 md:pt-16">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
                Common Questions
              </h2>
            </div>

            <div className="space-y-2.5">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="border border-gray-200 rounded-xl overflow-hidden transition-colors duration-200 hover:border-emerald-200"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 text-left cursor-pointer bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    <span className="font-medium text-gray-900 text-sm sm:text-base pr-4">{faq.q}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-gray-500 text-lg`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-5 bg-white">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: CTA ── */}
      <section className="py-24 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px]"
            style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(52,211,153,0.12) 0%, transparent 70%)' }}
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-5">
            Speed is Your Edge.
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Manual lead handling is the hidden bottleneck in your growth.&nbsp;Switch to high-performance automation and capture every opportunity.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/#re-contact"
              onClick={goToContact}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              Talk to Our Team
            </a>
          </div>
        </div>
      </section>



      <Footer />
    </div>
  );
}